"use client";
import React from 'react';

interface ContentItem {
  ctaText: string;
  description: string;
  media: any;
  link: string;
  color?: string;
}

const ContentCard = ({ ctaText, description, media, link, color, index }: ContentItem & { index: number }) => {
  return (
    <div className={`rounded-3xl p-5 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col justify-between align-center min-h-80`} style={{ backgroundColor: color ? color : '#f8fafc' }}>
      {/* Icon Section */}
      <div className="mb-6">
        <img
          src={media.data.attributes.url}
          alt={media.data.attributes.name}
          className="max-h-full max-w-full object-contain"
        />
      </div>

      {/* Title Section */}
      <div className="ml-6 mb-6">
        {/* <h3 className="text-2xl font-bold text-slate-800 leading-tight mb-4">
          {index % 2 === 0 ? 'Accelerating transformation with confidence' : 'Scaling quality across borders and business models'}
        </h3> */}
        <p className="text-slate-600 text-base leading-relaxed">
          {description}
        </p>
      </div>

      {/* CTA Section */}
      <div className="ml-6 mt-auto">
        <a
          href={link}
          className="inline-flex items-center text-slate-700 font-semibold hover:text-slate-900 transition-colors duration-200"
        >
          {ctaText}
          <svg
            className="ml-2 w-5 h-5 z-[1]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />

          </svg>
          <svg className="ml-[-12px] w-5 h-5" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="50" stroke="white" strokeWidth={4} fill="white" />
          </svg>
        </a>
      </div>
    </div>
  );
};

interface HeroProps {
  data?: {
    headingSectionsFeatures?: string;
    feature?: {
      id: string;
      title: string;
      description: string;
      url: string;
      media: any;
      text: string;
      colour?: string;
    }[];
    color?: string;
    redirectionUrl?: string;
  };
}

const ContentCardWrapper = ({ data }: HeroProps) => {
  // Add safety checks and default values
  if (!data || !data.feature || !Array.isArray(data.feature)) {
    return null;
  }

  const content = data.feature;
  const isSingleVideo =
    content.length === 1 &&
    content[0]?.url?.includes("youtube");

  const getYouTubeEmbeddedUrl = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;
    return `https://www.youtube.com/embed/${videoId}`;
  };

  return (
    <div
      id={`${(data.headingSectionsFeatures || 'content').split(' ').join('').toLowerCase()}`}
      className="py-20 px-6"
      style={{ background: data.color ? `${data.color}4A` : '#f8fafc' }}
    >
      {isSingleVideo ? (
        // Single video layout
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative w-full pb-[56.25%] mb-6">
            <iframe
              src={getYouTubeEmbeddedUrl(content[0].url)}
              className="absolute top-0 left-0 w-full h-full rounded-xl shadow-md"
              allowFullScreen
            />
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">
            {content[0].title}
          </h2>
          <p className="text-slate-600 text-lg">{content[0].description}</p>
        </div>
      ) : (
        // Multiple cards layout
        <div className="max-w-6xl mx-auto">
          {/* Heading + CTA */}
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-5xl font-bold text-[#032d60]">
              {data.headingSectionsFeatures || 'Featured Content'}
            </h2>

            {content.length > 2 && (
              <a href={data.redirectionUrl} className="text-[#032D60] font-semibold transition-colors duration-200 flex items-center">
                View All
                <svg className="ml-1 w-5 h-5 z-[1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                <svg className="ml-[-12px] w-5 h-5" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="50" stroke="white" strokeWidth={4} fill="#75CCFF4D" />
                </svg>
              </a>
            )}
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {content.slice(0, 2).map((item, index) => (
              <ContentCard
                key={item.id}
                ctaText={item.text}
                description={item.description}
                link={item.url}
                media={item.media}
                index={index}
                color={item.colour}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentCardWrapper;